import { z } from 'zod';
import { aiDraftKindEnum, aiDraftStatusEnum } from './ai-drafts.js';

export const AiIntent = {
  DATA_QUESTION: 'DATA_QUESTION',
  CHART_REQUEST: 'CHART_REQUEST',
  SUMMARY: 'SUMMARY',
  FORECAST: 'FORECAST',
  ANOMALY_CHECK: 'ANOMALY_CHECK',
  DRAFT_DOCUMENT: 'DRAFT_DOCUMENT',
  REFUSAL: 'REFUSAL',
  /** Read-only question about tasks/officers/boards (Phase 4 — Command Assistant). */
  TASK_QUESTION: 'TASK_QUESTION',
  /** Compose a DM or group message; AI returns a `message-draft` envelope. */
  MESSAGE_DRAFT: 'MESSAGE_DRAFT',
  /** Compose a new task; AI returns a `task-draft` envelope. */
  TASK_DRAFT: 'TASK_DRAFT',
  /**
   * Any other CRUD-except-delete action against boards/tasks/comments/
   * checklists/statuses/groups/labels. AI returns a generic `action-draft`
   * envelope discriminated by `kind`; the frontend renders a kind-specific
   * confirmation card. Covers all the in-app operations except deletes,
   * which remain CO-only via the manual UI.
   */
  ACTION_DRAFT: 'ACTION_DRAFT',
} as const;
export type AiIntent = (typeof AiIntent)[keyof typeof AiIntent];

export const AiMessageRole = {
  USER: 'USER',
  ASSISTANT: 'ASSISTANT',
  SYSTEM: 'SYSTEM',
} as const;
export type AiMessageRole = (typeof AiMessageRole)[keyof typeof AiMessageRole];

export const chartKindSchema = z.enum(['bar', 'line', 'pie']);

/**
 * Series and point keys are used as bracket-indexed object keys downstream
 * (the chart renderer merges series into a wide-form row keyed by `x`).
 * Block prototype-pollution candidates at the validation boundary so a
 * malicious LLM reply can't reach the renderer.
 */
const safeSeriesName = z
  .string()
  .min(1)
  .max(60)
  .regex(/^[^ $\[\]{}<>]+$/, 'series name contains forbidden characters')
  .refine(
    (s) => s !== '__proto__' && s !== 'constructor' && s !== 'prototype',
    'series name must not collide with object internals',
  );

const safePointX = z.union([
  z
    .string()
    .max(60)
    .regex(/^[^ $\[\]{}<>]*$/),
  z.number().finite(),
]);

export const chartSpecSchema = z.object({
  kind: chartKindSchema,
  title: z.string().min(1).max(120),
  xLabel: z.string().max(60).optional(),
  yLabel: z.string().max(60).optional(),
  series: z
    .array(
      z.object({
        name: safeSeriesName,
        points: z
          .array(
            z.object({
              x: safePointX,
              y: z.number().finite(),
            }),
          )
          .min(1)
          .max(60),
      }),
    )
    .min(1)
    .max(6),
});
export type ChartSpec = z.infer<typeof chartSpecSchema>;

export const aiMessageSchema = z.object({
  id: z.string().uuid(),
  conversationId: z.string().uuid(),
  role: z.enum(['USER', 'ASSISTANT', 'SYSTEM']),
  content: z.string(),
  chartSpec: chartSpecSchema.nullable(),
  intent: z.string().nullable(),
  tokensUsed: z.number().int().nullable(),
  model: z.string().nullable(),
  createdAt: z.string(),
  // Phase 4 — Command Assistant. When the assistant turn carries an actionable
  // draft (message to send, task to create), these fields are populated. The
  // frontend renders a confirmation card; the CO's click actuates the dispatch
  // via existing endpoints. `draftPayload` shape is discriminated by `draftKind`.
  draftKind: aiDraftKindEnum.nullable(),
  // The payload is one of ~28 schemas keyed by draftKind (see
  // `aiDraftSchemaByKind` in ai-drafts.ts). Validation happens at the
  // orchestrator extractDraft + dispatch layer; at this transport boundary
  // we keep it loose (any JSON object) so the frontend type stays simple.
  draftPayload: z.record(z.string(), z.unknown()).nullable(),
  draftStatus: aiDraftStatusEnum.nullable(),
  draftDispatchedRefId: z.string().uuid().nullable(),
  draftDispatchedAt: z.string().nullable(),
});
export type AiMessage = z.infer<typeof aiMessageSchema>;

export const aiConversationSchema = z.object({
  id: z.string().uuid(),
  title: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
  messageCount: z.number().int().nonnegative(),
});
export type AiConversation = z.infer<typeof aiConversationSchema>;

export const createConversationRequestSchema = z.object({
  title: z
    .string()
    .min(1, 'Title cannot be empty')
    .max(200, 'Title must be at most 200 characters')
    .optional(),
});
export type CreateConversationRequest = z.infer<typeof createConversationRequestSchema>;

export const sendMessageRequestSchema = z.object({
  content: z
    .string()
    .min(1, 'Message cannot be empty')
    .max(4000, 'Message must be at most 4000 characters'),
});
export type SendMessageRequest = z.infer<typeof sendMessageRequestSchema>;

export const usageQuerySchema = z.object({
  month: z
    .string()
    .regex(/^\d{4}-\d{2}$/, 'month must be YYYY-MM')
    .refine((v) => {
      const parts = v.split('-');
      const monthStr = parts[1];
      if (!monthStr) return false;
      const m = Number(monthStr);
      return m >= 1 && m <= 12;
    }, 'month component must be 01-12')
    .optional(),
});
export type UsageQuery = z.infer<typeof usageQuerySchema>;

export const aiSettingsKeyRequestSchema = z.object({
  apiKey: z
    .string()
    .min(20, 'OpenAI API keys are longer than 20 characters')
    .max(200, 'OpenAI API key looks too long; double-check the value')
    .regex(/^sk-/i, 'OpenAI API keys start with "sk-"'),
  monthlyTokenCap: z
    .number()
    .int('Monthly token cap must be a whole number')
    .positive('Monthly token cap must be greater than zero')
    .max(100_000_000, 'Monthly token cap cannot exceed 100,000,000')
    .optional(),
});
export type AiSettingsKeyRequest = z.infer<typeof aiSettingsKeyRequestSchema>;

export interface AiUsageResponse {
  month: string; // YYYY-MM Asia/Dhaka
  totalTokens: number;
  monthlyCap: number | null;
  percentUsed: number | null;
  warn80: boolean;
  byModel: { model: string; tokens: number; messages: number }[];
}

export interface AiSettingsStatus {
  configured: boolean;
  monthlyTokenCap: number | null;
}

// -------------------- AI Knowledge Hub --------------------

export const aiKnowledgeCategoryEnum = z.enum([
  'PRINCIPLE',
  'FACT',
  'PREFERENCE',
  'CONSTRAINT',
  'STRATEGY',
]);
export type AiKnowledgeCategory = z.infer<typeof aiKnowledgeCategoryEnum>;

export const aiKnowledgePriorityEnum = z.enum(['CRITICAL', 'HIGH', 'NORMAL']);
export type AiKnowledgePriority = z.infer<typeof aiKnowledgePriorityEnum>;

export const aiKnowledgeStatusEnum = z.enum(['ACTIVE', 'ARCHIVED']);
export type AiKnowledgeStatus = z.infer<typeof aiKnowledgeStatusEnum>;

export const aiKnowledgeCardSchema = z.object({
  id: z.string().uuid(),
  title: z.string().min(1).max(200),
  body: z.string().min(1).max(4000),
  category: aiKnowledgeCategoryEnum,
  priority: aiKnowledgePriorityEnum,
  status: aiKnowledgeStatusEnum,
  supersedesId: z.string().uuid().nullable(),
  createdBy: z.string().uuid(),
  createdByName: z.string().nullable().optional(),
  approvedAt: z.string(),
  archivedAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type AiKnowledgeCard = z.infer<typeof aiKnowledgeCardSchema>;

// The shape the LLM is asked to emit during `/ai/knowledge/propose`. Kept
// distinct from the persisted card so the propose UI can let the CO edit
// fields before final approval — nothing is written until `approve` lands.
export const proposedKnowledgeCardSchema = z.object({
  title: z.string().min(1).max(200),
  body: z.string().min(1).max(4000),
  category: aiKnowledgeCategoryEnum,
  priority: aiKnowledgePriorityEnum,
  // The LLM explains *why* it chose to extract this card so the CO can
  // sanity-check the synthesis. Free-form, capped to keep prompts small.
  rationale: z.string().min(1).max(600).optional(),
});
export type ProposedKnowledgeCard = z.infer<typeof proposedKnowledgeCardSchema>;

export const knowledgeConflictKindEnum = z.enum(['CONTRADICTION', 'OVERLAP', 'EXTENDS']);
export type KnowledgeConflictKind = z.infer<typeof knowledgeConflictKindEnum>;

export const knowledgeConflictReportSchema = z.object({
  newCardIndex: z.number().int().nonnegative(),
  existingCardId: z.string().uuid(),
  kind: knowledgeConflictKindEnum,
  reason: z.string().min(1).max(600),
});
export type KnowledgeConflictReport = z.infer<typeof knowledgeConflictReportSchema>;

// The full JSON contract the propose-LLM must satisfy.
export const knowledgeProposalResponseSchema = z.object({
  proposedCards: z.array(proposedKnowledgeCardSchema).min(0).max(20),
  conflicts: z.array(knowledgeConflictReportSchema).max(60),
});
export type KnowledgeProposalResponse = z.infer<typeof knowledgeProposalResponseSchema>;

export const proposeKnowledgeRequestSchema = z.object({
  rawText: z
    .string()
    .min(1, 'Please paste at least one sentence of guidance.')
    .max(4000, 'Submission must be at most 4000 characters.'),
});
export type ProposeKnowledgeRequest = z.infer<typeof proposeKnowledgeRequestSchema>;

export const knowledgeConflictActionEnum = z.enum(['KEEP', 'SUPERSEDE', 'ARCHIVE']);
export type KnowledgeConflictAction = z.infer<typeof knowledgeConflictActionEnum>;

export const knowledgeConflictResolutionSchema = z.object({
  newCardIndex: z.number().int().nonnegative(),
  existingCardId: z.string().uuid(),
  action: knowledgeConflictActionEnum,
});
export type KnowledgeConflictResolution = z.infer<typeof knowledgeConflictResolutionSchema>;

export const approveKnowledgeRequestSchema = z.object({
  cards: z.array(proposedKnowledgeCardSchema).min(1, 'Approve at least one card.').max(20),
  conflictResolutions: z.array(knowledgeConflictResolutionSchema).max(60).default([]),
});
export type ApproveKnowledgeRequest = z.infer<typeof approveKnowledgeRequestSchema>;

export const listKnowledgeQuerySchema = z.object({
  status: aiKnowledgeStatusEnum.optional(),
  page: z.coerce.number().int().min(1).max(100).default(1).optional(),
  pageSize: z.coerce.number().int().min(1).max(100).default(50).optional(),
});
export type ListKnowledgeQuery = z.infer<typeof listKnowledgeQuerySchema>;

export const updateConversationTitleRequestSchema = z.object({
  title: z
    .string()
    .min(1, 'Title cannot be empty.')
    .max(200, 'Title must be at most 200 characters.'),
});
export type UpdateConversationTitleRequest = z.infer<typeof updateConversationTitleRequestSchema>;
